import { gql } from '@apollo/client';
import React, { useState } from 'react';
import { Link } from '../../utils/router/v3Bridge';
import Button from '../../components/Button';
import type { HistoricalComment_CommentFragment } from '../../graphql/generated/graphqlTypes';
import userPath from '../../utils/url/userPath';
import MagneticWallItemAvatar from '../MagneticWallItemAvatar';
import CommentAuthor from './CommentAuthor';
import CommentPreview from './CommentPreview';

const MAX_LINE_COUNT = 4;

const COMMENT_FRAGMENT = gql`
  fragment HistoricalComment_comment on Comment {
    id
    content
    author {
      ...userPath_user
      ...MagneticWallItemAvatar_user
      ...CommentAuthor_author
    }
    insertedAt
  }
  ${userPath.fragments.user}
  ${MagneticWallItemAvatar.fragments.user}
  ${CommentAuthor.fragments.author}
`;

interface HistoricalCommentProps {
  comment: HistoricalComment_CommentFragment;
  threadId: string;
}

const HistoricalComment = ({ comment, threadId }: HistoricalCommentProps) => {
  const [isShowingMore, setIsShowingMore] = useState(false);
  const [canBeClamped, setCanBeClamped] = useState<boolean | null>(null);

  const toggleIsShowingMore = () => {
    setIsShowingMore((val) => !val);
  };

  if (!comment) return null;
  return (
    <div className="comment">
      <div className="comment-left">
        <Link to={userPath(comment.author)}>
          <MagneticWallItemAvatar user={comment.author} />
        </Link>
      </div>
      <div className="comment-right">
        <div className="comment-meta">
          <CommentAuthor
            author={comment.author}
            insertedAt={comment.insertedAt}
            threadId={threadId}
            isHistorical
          />
        </div>
        <CommentPreview
          content={comment.content}
          maxLines={MAX_LINE_COUNT}
          disableLineClamp={isShowingMore}
          onCanBeClampedChange={setCanBeClamped}
        />
        {canBeClamped && (
          <div className="comment-actions">
            <Button variant="link" size="micro" onClick={toggleIsShowingMore}>
              {isShowingMore ? 'Show less' : 'Show more'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

HistoricalComment.fragments = { comment: COMMENT_FRAGMENT };

export default HistoricalComment;
